import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { authClient } from "@/lib/auth-client"
import { tripSlug } from "@/lib/slugify"
import { Attribution } from "@/components/AttributionPopover"
import { defaultMeta, noIndex } from "@/lib/seo"

export const Route = createFileRoute(
  `/_with-optional-sidebar/trip/share/$shareToken`
)({
  head: () =>
    defaultMeta({
      title: `Trip Invitation`,
      description: `Join a private ChicTravel trip invitation.`,
      path: `/trip/share`,
      robots: noIndex,
    }),
  component: ShareLandingPage,
})

function ShareLandingPage() {
  const { shareToken } = Route.useParams()
  const navigate = useNavigate()
  const { data: session, isPending: sessionLoading } = authClient.useSession()
  const [tripData, setTripData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [joining, setJoining] = useState(false)
  const [stateReady, setStateReady] = useState(false)

  // Public invite preview cannot use an authenticated Electric collection.
  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const res = await fetch(`/api/share/${shareToken}/preview`)
        if (!res.ok) {
          setError(`Trip not found or invite expired`)
          setStateReady(true)
          setLoading(false)
          return
        }
        const data = await res.json()
        setTripData(data)
        setStateReady(true)
      } catch (err) {
        console.error(`Error fetching preview:`, err)
        setError(`Failed to load trip`)
        setStateReady(true)
        setLoading(false)
      }
    }

    fetchPreview()
  }, [shareToken])

  // Handle navigation/join logic after we know the state
  useEffect(() => {
    if (!stateReady || !tripData || sessionLoading) return

    // If user is logged in, check their status
    if (session?.user) {
      // Relationship is resolved server-side (the preview no longer exposes
      // owner_id / shared_user_ids).
      const isOwner = tripData.is_owner
      const isCollaborator = tripData.is_collaborator

      // Owner or already a collaborator - navigate away
      if (isOwner || isCollaborator) {
        navigate({
          to: `/trip/$slug/$tripId`,
          params: { slug: tripSlug(tripData), tripId: tripData.short_id },
        })
      }
      // New collaborator - auto join
      else if (!joining) {
        joinTrip()
      }
    }
    // If not logged in, show the landing page
    else {
      setLoading(false)
    }
  }, [stateReady, tripData, session, sessionLoading, joining, navigate])

  const joinTrip = async () => {
    try {
      setJoining(true)
      const res = await fetch(`/api/share/${shareToken}/join`, {
        method: `POST`,
      })

      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.error || `Failed to join trip`)
      }

      navigate({
        to: `/trip/$slug/$tripId`,
        params: { slug: tripSlug(tripData), tripId: tripData.short_id },
      })
    } catch (err) {
      console.error(`Error joining trip:`, err)
      setError(
        `Failed to join trip. ${err instanceof Error ? err.message : `Please try again.`}`
      )
      setJoining(false)
      setLoading(false)
    }
  }

  const handleSignIn = () => {
    navigate({
      to: `/login`,
      search: { redirect: `/trip/share/${shareToken}` },
    })
  }

  const handleJoin = async () => {
    if (!tripData) return
    await joinTrip()
  }

  const shouldShowLoader = loading || sessionLoading

  if (shouldShowLoader) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-muted-foreground">Loading trip...</p>
        </div>
      </div>
    )
  }

  if (error || !tripData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Oops!</h1>
          <p className="text-muted-foreground">{error || `Trip not found`}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/10">
      {/* Header */}
      <div className="border-b border-border bg-background/50 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto px-6 py-6">
          <p className="text-sm text-muted-foreground mb-2">
            You've been invited to collaborate on
          </p>
          <h1 className="text-4xl font-bold">
            {tripData.name || tripData.destination}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Trip Details Card */}
        <div className="bg-card border border-border rounded-lg p-8 mb-8">
          {tripData.image_url && (
            <div className="relative mb-6 rounded-lg overflow-hidden">
              <img
                src={tripData.image_url}
                alt={tripData.destination}
                className="w-full h-64 object-cover"
              />
              {tripData.image_attribution && (
                <Attribution
                  attribution={tripData.image_attribution}
                  attributionUrl={tripData.image_attribution_url}
                />
              )}
            </div>
          )}

          <div className="space-y-4 mb-8">
            <div>
              <h2 className="text-lg font-semibold">Destination</h2>
              <p className="text-foreground">{tripData.destination}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">
                  Dates
                </h3>
                <p className="text-foreground">
                  {tripData.start_date && tripData.end_date
                    ? `${new Date(tripData.start_date).toLocaleDateString()} – ${new Date(
                        tripData.end_date
                      ).toLocaleDateString()}`
                    : `Dates TBD`}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">
                  Duration
                </h3>
                <p className="text-foreground">
                  {tripData.start_date && tripData.end_date
                    ? `${Math.ceil(
                        (new Date(tripData.end_date).getTime() -
                          new Date(tripData.start_date).getTime()) /
                          (1000 * 60 * 60 * 24)
                      )} days`
                    : `—`}
                </p>
              </div>
            </div>

            {tripData.description && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">
                  About
                </h3>
                <p className="text-foreground">{tripData.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          {session?.user ? (
            <button
              onClick={handleJoin}
              disabled={joining}
              className="px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 font-medium text-lg"
            >
              {joining ? `Joining...` : `Join to Collaborate`}
            </button>
          ) : (
            <>
              <button
                onClick={handleSignIn}
                className="px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium text-lg"
              >
                Sign In to Collaborate
              </button>
              <p className="text-sm text-muted-foreground mt-4">
                After signing in, you'll be added as a collaborator
                automatically.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
